// utils/csvExport.ts
export type ExportFormat = 'csv' | 'excel' | 'pdf';

export function exportToCsv<T extends Record<string, any>>(
    filename: string,
    data: T[],
    headers?: { label: string; key: keyof T | ((row: T) => any) }[],
    format: ExportFormat = 'csv'
) {
    if (!data || data.length === 0) {
        console.warn("No data to export");
        return;
    };

    // build header row
    const headerRow = headers
        ? headers.map(h => h.label)
        : Object.keys(data[0]);

    // build body rows
    const bodyRows = data.map(row =>
        (headers
            ? headers.map(h =>
                typeof h.key === "function" ? h.key(row) : row[h.key]
            )
            : Object.values(row)
        ).map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`)
    );

    const csvContent = [headerRow, ...bodyRows]
        .map(e => e.join(","))
        .join("\n");

    // Determine file type and extension based on format
    let mimeType: string;
    let fileExtension: string;
    
    switch (format) {
        case 'excel':
            mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            fileExtension = "xlsx";
            break;
        case 'pdf':
            mimeType = "application/pdf";
            fileExtension = "pdf";
            break;
        case 'csv':
        default:
            mimeType = "text/csv;charset=utf-8;";
            fileExtension = "csv";
            break;
    }

    // Update filename with correct extension
    const finalFilename = filename.endsWith(`.${fileExtension}`) 
        ? filename 
        : `${filename}.${fileExtension}`;

    // trigger download
    const blob = new Blob([csvContent], { type: mimeType });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", finalFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};